import { useState, useCallback } from 'react'
import { DragEndEvent } from '@dnd-kit/core'
import { arrayMove } from '@dnd-kit/sortable'

type UseSortableListProps<T> = {
  items: T[]
  onReorder: (reorderedItems: T[]) => void
  getId: (item: T) => string | number
}

export const useSortableList = <T>({
  items,
  onReorder,
  getId,
}: UseSortableListProps<T>) => {
  const [activeId, setActiveId] = useState<string | number | null>(null)

  const handleDragStart = useCallback((event: DragEndEvent) => {
    setActiveId(event.active.id)
  }, [])

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event

      setActiveId(null)

      if (!over || active.id === over.id) {
        return
      }

      const oldIndex = items.findIndex(item => getId(item) === active.id)
      const newIndex = items.findIndex(item => getId(item) === over.id)

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedItems = arrayMove(items, oldIndex, newIndex)
        onReorder(reorderedItems)
      }
    },
    [items, onReorder, getId]
  )

  const handleDragCancel = useCallback(() => {
    setActiveId(null)
  }, [])

  return {
    activeId,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
  }
}
